import {Injectable} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {LoaderService} from "./loader.service";
import {EnvironmentService} from "./environment.service";
import {Observable} from "rxjs";
import {ActionResult, EventItem, LeagueEventType, MultiLangField, LeagueEvents} from "@model";
import {map, tap} from "rxjs/operators";

@Injectable({ providedIn: 'root' })
export class LeagueEventsService {
    constructor(
        private http: HttpClient,
        private loaderService: LoaderService,
        private environmentService: EnvironmentService,
    ) {}

    getLeagueEventsProtected(leagueId: number): Observable<LeagueEvents | undefined> {
        return this.http.get<ActionResult<LeagueEvents | undefined>>(
            `${this.environmentService.getApiUrl()}/api/leagues/${leagueId}/events`
        ).pipe(map(r => r.result ? Object.assign(new LeagueEvents(), r.result) : undefined));

    }

    getLeagueEvents(leagueId: number): Observable<LeagueEvents | undefined> {
        return this.http.get<ActionResult<LeagueEvents | undefined>>(
            `${this.environmentService.getApiUrl()}/api/public/leagues/${leagueId}/events`
        ).pipe(map(r => r.result ? Object.assign(new LeagueEvents(), r.result) : undefined));
    }

    availableEventsForLeague(leagueId?: number): Observable<EventItem[]> {
        this.loaderService.startLoading();
        return this.http.post<ActionResult<EventItem[]>>(
            `${this.environmentService.getApiUrl()}/api/leagues/${leagueId}/events/available`, undefined
        ).pipe(
            tap(() => this.loaderService.dismissLoading()),
            map(value => value?.result?.length ? value.result : [])
        );
    }

    addEventsToLeague(leagueId: number, events: EventItem[], onSuccess: () => void, onError: () => void): void {
        this.http.post<ActionResult<boolean>>(
            `${this.environmentService.getApiUrl()}/api/leagues/${leagueId}/events/add`, events
        ).subscribe(actionResult => {
            actionResult.success ? onSuccess() : onError();
        });
    }

    addEventTemplate(leagueId: number, event: EventItem, onSuccess: () => void, onError: () => void): void {
        this.http.post<ActionResult<boolean>>(
            `${this.environmentService.getApiUrl()}/api/leagues/${leagueId}/events/template/add`, event
        ).subscribe(actionResult => {
            actionResult.success ? onSuccess() : onError();
        });
    }

    editEventTemplate(leagueId: number, event: EventItem, onSuccess: () => void, onError: () => void): void {
        if (!event.id) return;
        this.http.post<ActionResult<boolean>>(
            `${this.environmentService.getApiUrl()}/api/leagues/${leagueId}/events/template/edit/${event.id}`, event
        ).subscribe(actionResult => {
            actionResult.success ? onSuccess() : onError();
        });
    }

    getEventTemplate(eventId: number): Observable<EventItem | undefined> {
        return this.http.get<ActionResult<EventItem>>(
            `${this.environmentService.getApiUrl()}/api/leagues/events/template/${eventId}`
        ).pipe(
            map(value => value.result)
        );
    }

    deleteEventTemplate(leagueId: number, eventId: number, onSuccess: () => void, onError: () => void): void {
        this.http.delete<ActionResult<boolean>>(
            `${this.environmentService.getApiUrl()}/api/leagues/${leagueId}/events/template/delete/${eventId}`
        ).subscribe(actionResult => {
            actionResult.success ? onSuccess() : onError();
        });
    }

    removeEventFromLeague(eventId: number, leagueId: number, onSuccess: () => void, onError: () => void): void {
        this.http.post<ActionResult<boolean>>(
            `${this.environmentService.getApiUrl()}/api/leagues/${leagueId}/events/remove/${eventId}`, undefined
        ).subscribe(actionResult => {
            actionResult.success ? onSuccess() : onError();
        });
    }

    removeRaceFromLeague(raceId: number, leagueId: number, onSuccess: () => void, onError: () => void): void {
        this.http.post<ActionResult<boolean>>(
            `${this.environmentService.getApiUrl()}/api/leagues/${leagueId}/races/remove/${raceId}`, undefined
        ).subscribe(actionResult => {
            actionResult.success ? onSuccess() : onError();
        });
    }

    updateEventType(eventId: number, leagueId: number, isTemplate: boolean, type: LeagueEventType, onSuccess: () => void, onError: () => void): void {
        const url = isTemplate
            ? `${this.environmentService.getApiUrl()}/api/leagues/${leagueId}/events/template/${eventId}/type`
            : `${this.environmentService.getApiUrl()}/api/leagues/${leagueId}/events/${eventId}/type`;
        this.http.post<ActionResult<boolean>>(url, type)
            .subscribe(r => r.success ? onSuccess() : onError());
    }

    updateLeagueEventShortName(eventId: number, leagueId: number, shortName: MultiLangField, onSuccess: () => void, onError: () => void): void {
        const url = `${this.environmentService.getApiUrl()}/api/leagues/${leagueId}/events/${eventId}/short-name`;
        this.http.post<ActionResult<boolean>>(url, shortName)
            .subscribe(r => r.success ? onSuccess() : onError());
    }

}